import {
    DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
    DEFAULT_TOUR_DURATION_MINUTES,
} from "@sp-crm/core";
import { CalendarItem, EventCalendarDto } from "./calendar-types";
import { parseDateTimeToTimeStr } from "./calendar-utils";

const eventLocalTimeStr = (event: EventCalendarDto): string =>
    new Date(event.startsAt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: event.timeZone,
    });

export const displayLabel = (item: CalendarItem): string => {
    switch (item.kind) {
        case "tour":
            return item.data.communityName;
        case "task":
            return item.data.text;
        case "calendarEvent": {
            const parent =
                item.data.clientName ??
                item.data.businessName ??
                item.data.parentContactName ??
                "Referral source";
            return item.data.title || `Visit — ${parent}`;
        }
        case "event":
            return item.data.title;
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const displayTimeStr = (item: CalendarItem): string | null => {
    switch (item.kind) {
        case "tour":
            return item.data.tourTime;
        case "task":
            return parseDateTimeToTimeStr(item.data.dueDateTime);
        case "calendarEvent":
            return item.data.eventTime;
        case "event":
            return eventLocalTimeStr(item.data);
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const monthShowsTime = (item: CalendarItem): boolean => {
    switch (item.kind) {
        case "tour":
        case "calendarEvent":
        case "event":
            return true;
        case "task":
            return false;
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const displayDurationMinutes = (item: CalendarItem): number => {
    switch (item.kind) {
        case "tour":
            return item.data.tourDuration ?? DEFAULT_TOUR_DURATION_MINUTES;
        case "calendarEvent":
        case "event":
            return item.data.eventDuration;
        case "task":
            return DEFAULT_CALENDAR_EVENT_DURATION_MINUTES;
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const displayTestId = (item: CalendarItem): string => {
    switch (item.kind) {
        case "tour":
            return "calendar-tour-event";
        case "task":
            return "calendar-task-event";
        case "calendarEvent":
            return "calendar-visit-event";
        case "event":
            return "calendar-marketing-event";
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const displayId = (item: CalendarItem): string => item.data.id;

export const allDayBlockClasses = (item: CalendarItem): string => {
    switch (item.kind) {
        case "tour":
            return "bg-brand-600 text-white hover:bg-brand-700";
        case "task":
            return "bg-emerald-50 text-emerald-700 hover:bg-emerald-100";
        case "calendarEvent":
            return "bg-purple-600 text-white hover:bg-purple-700";
        case "event":
            return "bg-amber-600 text-white hover:bg-amber-700";
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const timedBlockClasses = (item: CalendarItem): string => {
    switch (item.kind) {
        case "tour":
            return "bg-brand-600 hover:bg-brand-700";
        case "task":
            return "bg-emerald-600 hover:bg-emerald-700";
        case "calendarEvent":
            return "bg-purple-600 hover:bg-purple-700";
        case "event":
            return "bg-amber-600 hover:bg-amber-700";
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const timedAccentClasses = (item: CalendarItem): string => {
    switch (item.kind) {
        case "tour":
            return "text-brand-100";
        case "task":
            return "text-emerald-100";
        case "calendarEvent":
            return "text-purple-100";
        case "event":
            return "text-amber-100";
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};

export const monthPillClasses = (item: CalendarItem): string => {
    switch (item.kind) {
        case "tour":
            return "bg-brand-50 text-brand-700 hover:bg-brand-100";
        case "task":
            return "bg-emerald-50 text-emerald-700 hover:bg-emerald-100";
        case "calendarEvent":
            return "bg-purple-50 text-purple-700 hover:bg-purple-100";
        case "event":
            return "bg-amber-50 text-amber-700 hover:bg-amber-100";
        default: {
            const exhaustiveCheck: never = item;
            throw new Error(`Unhandled CalendarItem kind: ${exhaustiveCheck}`);
        }
    }
};
